import React, { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { SkeletonCards } from '../Components/FeedList'
import { ArrowLeft, ChevronLeft, ExternalIcon } from '../Components/Icons'
import MainStoryCard from '../Components/MainStoryCard'
import { useBack } from '../Components/PageHeader'
import { SourceAvatar, SourceStack } from '../Components/SourceAvatar'
import StoryCard from '../Components/StoryCard'
import { useDocumentTitle } from '../Hooks/useDocumentTitle'
import { t } from '../i18n'
import { api } from '../Services/api'
import { useFollow } from '../State/Follow'
import { entryOf, track, useSurface } from '../Utils/analytics'
import { articleOpened } from '../Utils/article'
import { timeAgo } from '../Utils/time'

const SHOWN_SOURCES = 10
const cache = new Map()

function useStory(id, followed) {
  const key = `${id}:${followed.join(',')}`
  const [state, setState] = useState(() => cache.get(key) || { status: 'loading', data: null })
  useEffect(() => {
    let alive = true
    const hit = cache.get(key)
    if (hit && Date.now() - hit.at < 60000) {
      setState(hit)
      return undefined
    }
    if (!hit) setState({ status: 'loading', data: null })
    api
      .story(id, { followed })
      .then((data) => {
        const next = { status: 'ready', data, at: Date.now() }
        cache.set(key, next)
        if (alive) setState(next)
      })
      .catch((error) => {
        if (!alive) return
        setState({ status: error?.response?.status === 404 ? 'missing' : 'error', data: null })
      })
    return () => {
      alive = false
    }
    // followed is part of key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, id])
  return state
}

function OtherSource({ article, story }) {
  const surface = useSurface()
  return (
    <li>
      <a
        className="other-source"
        href={article.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${article.title} – ${t('story.openArticle', { name: article.source.name })}`}
        onClick={() => articleOpened({ story, article, entry: 'others', surface })}
      >
        <span className="other-source-text">
          <span className="other-source-meta">
            <SourceAvatar source={article.source} size={20} />
            <strong>{article.source.name}</strong>
            {article.isFollowed && <span className="tag-following">{t('card.following')}</span>}
            <span className="card-dot" aria-hidden="true">
              ·
            </span>
            <time dateTime={article.publishedAt}>{timeAgo(article.publishedAt)}</time>
          </span>
          <span className="other-source-title">{article.title}</span>
        </span>
        <ExternalIcon size={18} className="muted-icon" />
      </a>
    </li>
  )
}

/* "Drugi pišu" screen (spec 5.5): every source's headline, no pictures. While
   the story is developing the API sends them newest first. */
function StorySources({ story }) {
  const back = useBack()
  const [expanded, setExpanded] = useState(false)
  const others = story.others || []
  const shown = expanded ? others : others.slice(0, SHOWN_SOURCES)
  return (
    <>
      <header className="page-header story-header">
        <button
          type="button"
          className="icon-button back-button"
          aria-label={t('common.back')}
          onClick={back}
        >
          <ArrowLeft size={22} />
        </button>
        <SourceStack sources={story.sourcePreview} size={22} />
        <h1 className="page-title">{t('story.header', { count: story.sourceCount })}</h1>
      </header>
      <div className="feed-item">
        <MainStoryCard story={story} variant="story" />
      </div>
      {others.length > 0 && (
        <section className="others-section">
          <h2 className="section-title">{t('story.othersWrite')}</h2>
          <ul className="other-sources">
            {shown.map((a) => (
              <OtherSource key={a.id} article={a} story={story} />
            ))}
          </ul>
          {!expanded && others.length > SHOWN_SOURCES && (
            <button
              type="button"
              className="btn btn-secondary btn-block"
              onClick={() => {
                track('story_more_sources', { story_id: story.id, shown_count: SHOWN_SOURCES })
                setExpanded(true)
              }}
            >
              {t('story.showMore', { count: others.length - SHOWN_SOURCES })}
            </button>
          )}
        </section>
      )}
    </>
  )
}

/* Someone opened a shared link (spec 5.5, "Ekran podijeljenog linka"): the
   story as the Glavna novost card, then six related stories. */
function SharedStory({ story, related }) {
  return (
    <>
      <header className="shared-header">
        <Link to="/" className="home-back">
          <ChevronLeft size={20} strokeWidth={2.5} />
          <span>{t('common.home')}</span>
        </Link>
      </header>
      <div className="feed-item">
        <MainStoryCard story={story} variant="shared" />
      </div>
      {related.length > 0 && (
        <section>
          <h2 className="section-title section-title-feed">{t('story.readAlso')}</h2>
          {related.map((s, i) => (
            <div className="feed-item" key={s.id}>
              <StoryCard story={s} entry="read_also" position={i + 1} />
            </div>
          ))}
        </section>
      )}
    </>
  )
}

export default function Story() {
  const { id } = useParams()
  const location = useLocation()
  const { sources: followed } = useFollow()
  // First screen of this visit = came from outside (a shared link).
  const shared = location.key === 'default'
  const { status, data } = useStory(id, followed)
  useDocumentTitle(data?.story?.title || null)

  // story_open, or shared_link_view for a visit that starts here (spec 5.2).
  const ready = status === 'ready'
  useEffect(() => {
    if (!ready) return
    const params = new URLSearchParams(location.search)
    if (shared) {
      track('shared_link_view', {
        story_id: id,
        utm_source: params.get('utm_source'),
        utm_medium: params.get('utm_medium'),
      })
    } else {
      track('story_open', {
        story_id: id,
        source_count: data.story.sourceCount,
        entry: entryOf(location),
      })
    }
    // Once per story shown; the rest is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ready])

  if (status === 'loading') return <SkeletonCards count={3} main />
  if (status !== 'ready') {
    return (
      <div className="feed-message">
        <p>{status === 'missing' ? t('story.notFound') : t('feed.error')}</p>
        <Link to="/" className="btn btn-secondary">
          {t('notFound.home')}
        </Link>
      </div>
    )
  }
  return shared ? (
    <SharedStory story={data.story} related={data.related || []} />
  ) : (
    <StorySources story={data.story} />
  )
}
